import type { LucideIcon } from "lucide-react"
import { useId } from "react"
import { Tabs, TabsList, TabsTrigger } from "@/primitives/tabs.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { shortcutOf, type ShortcutId } from "@/shortcuts/registry.ts"
import { Kbd } from "./key.tsx"

/*
 * One choice among a few, every choice in view: Spend's range, a model's Effort, the colour scheme.
 * It looks like the segmented Tabs, but it is one setting and has no panels, so a screen reader
 * hears a radio group ("Medium, radio button, 3 of 5, checked"); an arrow key moves the choice.
 */

/**
 * A choice's `label` is what its segment reads, after its `icon` when it has one; `shortcut`, when
 * the page binds one to it, is named with its key in the segment's tooltip.
 */
export type SegmentedOption<Value extends string> = {
  value: Value
  label: string
  icon?: LucideIcon
  shortcut?: ShortcutId
}

/**
 * `label` names the group for a screen reader. `disabledReason` shows the control as it stands but
 * takes no choice, and says why in a tooltip, as a model with no Effort to choose does; a segment
 * that takes the focus is described by the same reason, so a keyboard hears it too.
 */
export function Segmented<Value extends string>({
  label,
  value,
  options,
  onChange,
  disabledReason,
}: {
  label: string
  value: Value | null
  options: readonly SegmentedOption<Value>[]
  onChange: (value: Value) => void
  disabledReason?: string
}) {
  const disabled = disabledReason !== undefined
  const reason = useId()
  const control = (
    <Tabs
      value={value}
      onValueChange={(next: unknown) => {
        const chosen = options.find((option) => option.value === next)
        if (chosen && !disabled) onChange(chosen.value)
      }}
    >
      <TabsList
        variant="segmented"
        role="radiogroup"
        aria-label={label}
        aria-disabled={disabled || undefined}
        activateOnFocus
      >
        {options.map((option) => {
          const segment = (
            <TabsTrigger
              key={option.value}
              value={option.value}
              disabled={disabled}
              role="radio"
              aria-checked={option.value === value}
              aria-selected={undefined}
              aria-describedby={disabled ? reason : undefined}
            >
              {option.icon && <option.icon aria-hidden />}
              {option.label}
            </TabsTrigger>
          )
          if (!option.shortcut || disabled) return segment
          return (
            <Tooltip key={option.value}>
              <TooltipTrigger render={segment} />
              <TooltipContent>
                {shortcutOf(option.shortcut).label}
                <Kbd shortcut={option.shortcut} />
              </TooltipContent>
            </Tooltip>
          )
        })}
      </TabsList>
    </Tabs>
  )
  if (!disabled) return control
  // A disabled segment takes no pointer, so the tooltip hangs on the control around it.
  return (
    <Tooltip>
      <TooltipTrigger render={<span className="inline-flex w-fit" />}>
        {control}
        <span id={reason} className="sr-only">
          {disabledReason}
        </span>
      </TooltipTrigger>
      <TooltipContent>{disabledReason}</TooltipContent>
    </Tooltip>
  )
}
